import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/api';
import GiftCardTemplateList from './GiftCardTemplateList';
import GiftCardTemplateForm from './GiftCardTemplateForm';
import {
  emptyGiftCardTemplateForm,
  formFromTemplate,
  buildGiftCardTemplateApiPayload,
  getErrorMessage,
  getErrorStatus,
} from '../../utils/giftCardTemplateForm';

/**
 * Lot 4B/4C — Écran Admin Cartes cadeaux (CRUD templates, catalogVisible Admin).
 * Tip : accessible pour tous les commerçants (nav core) ; aucune carte auto-créée.
 */
export default function CartesCadeauxManager({ commercantId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyGiftCardTemplateForm());
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    if (!commercantId) {
      setError('Identifiant commerçant manquant');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await apiService.listGiftCardTemplates(commercantId);
      setItems(Array.isArray(payload?.items) ? payload.items : []);
    } catch (err) {
      const status = getErrorStatus(err);
      setError(
        getErrorMessage(
          err,
          status === 403
            ? 'Accès refusé aux cartes cadeaux'
            : 'Impossible de charger les cartes cadeaux',
        ),
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    load();
  }, [load]);

  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setEditingId(null);
    setFormErrors({});
    setFormError(null);
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setForm(emptyGiftCardTemplateForm({ validityPreset: 'M12' }));
    setFormErrors({});
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (template) => {
    setFormMode('edit');
    setEditingId(template.id);
    setForm(formFromTemplate(template));
    setFormErrors({});
    setFormError(null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    const built = buildGiftCardTemplateApiPayload(form);
    if (!built.ok) {
      setFormErrors(built.errors || {});
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (formMode === 'create') {
        await apiService.createGiftCardTemplate({
          ...built.payload,
          commercantId,
        });
        showToast('Carte cadeau créée');
      } else {
        await apiService.updateGiftCardTemplate(editingId, built.payload);
        showToast('Carte cadeau mise à jour');
      }
      closeForm();
      await load();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Enregistrement impossible'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActif = async (template) => {
    setBusyId(template.id);
    try {
      await apiService.setGiftCardTemplateStatus(template.id, !template.actif);
      showToast(template.actif ? 'Carte désactivée' : 'Carte réactivée');
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'Action impossible'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="cartes-cadeaux-manager">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Cartes cadeaux</h2>
          <p className="text-sm text-gray-600">
            Proposez des cartes cadeaux à vos clients.
          </p>
        </div>
        {!formOpen && (
          <button type="button" className="btn-primary" onClick={openCreate}>
            Créer une carte cadeau
          </button>
        )}
      </div>

      {toast && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
          {toast}
        </div>
      )}

      {formOpen ? (
        <GiftCardTemplateForm
          mode={formMode}
          form={form}
          errors={formErrors}
          submitting={submitting}
          formError={formError}
          onChange={setForm}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      ) : (
        <>
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'all', label: 'Toutes' },
              { id: 'active', label: 'Actives' },
              { id: 'inactive', label: 'Inactives' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  filter === f.id
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-300 text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          ) : (
            <GiftCardTemplateList
              items={items}
              filter={filter}
              busyId={busyId}
              onEdit={openEdit}
              onToggleActif={handleToggleActif}
            />
          )}
        </>
      )}
    </div>
  );
}
